import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, Eye } from 'lucide-react';
import { gdImport } from '../../lib/api';
import { readFileAsText } from '../../lib/importExport';
import { toast } from 'sonner';

// Bringing a GorillaDesk invoice or payment export in as what it actually is.
//
// The generic Import / Export above this reads a CSV whose headers already
// match ours. These two exports do not, and more importantly they need
// reconciling rather than inserting: GorillaDesk is authoritative for an
// invoice total, an invoice already on file must be updated and not duplicated,
// and a payment has to find the invoice it settles. That work lives on the
// server; this screen is the part that was missing, because until now those two
// endpoints could only be reached by someone who could type a POST request.
//
// Nothing is written until a check has been run on the very same text. The
// check is not a formality: it is where you see how many rows matched nobody,
// which is the difference between an import and a mess.

const GREEN = '#8dd110';

const KINDS = {
  invoices: {
    label: 'Invoices',
    call: gdImport.invoices,
    hint: 'The export with Invoice #, Customer, Date, Total and Status. '
        + 'Do this one first: a payment can only be matched to an invoice that is already on file.',
    counts: (r) => [
      ['Rows read', r.rowsRead],
      ['New invoices', r.toInsert],
      ['Totals to correct', r.toUpdate],
      ['Already right', r.unchanged],
      ['Rows skipped', r.skipped],
      ['Value of the new ones', money(r.insertValue)],
    ],
    warnings: (r) => (r.unmatchedAccounts?.length
      ? [`${r.unmatchedAccounts.length} account number(s) matched no customer: `
         + r.unmatchedAccounts.slice(0, 8).join(', ')]
      : []),
  },
  payments: {
    label: 'Payments',
    call: gdImport.payments,
    hint: 'The export with Payment For, Customer, Method, Date and Amount. '
        + 'Run it after the invoices, and it will correct the balances they carry.',
    counts: (r) => [
      ['Rows read', r.rowsRead],
      ['New payments', r.paymentsToInsert],
      ['Value', money(r.paymentValue)],
      ['Invoice balances to fix', r.invoiceBalancesToFix],
      ['Credits not applied', r.unappliedCredits],
      ['Rows skipped', r.skipped],
    ],
    warnings: (r) => [
      r.orphanPayments ? `${r.orphanPayments} payment(s) worth ${money(r.orphanValue)} name an invoice that is not on file. They are kept, against the customer, so the money is not lost.` : '',
      r.unlinkedToCustomer ? `${r.unlinkedToCustomer} payment(s) could not be tied to a customer at all.` : '',
    ].filter(Boolean),
  },
};

function money(v) {
  return '$' + (Number(v) || 0).toLocaleString(undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function GorillaDeskImport() {
  const [kind, setKind] = useState('invoices');
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState('');
  // The text the check was run against. An edit after a check clears the
  // result, so "Import for real" can never commit something nobody has seen.
  const [checked, setChecked] = useState(null);
  const [result, setResult] = useState(null);
  const spec = KINDS[kind];

  const switchKind = (k) => { setKind(k); setChecked(null); setResult(null); };
  const onText = (text) => { setCsv(text); setChecked(null); setResult(null); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { onText(await readFileAsText(file)); toast.success(`Loaded ${file.name}`); }
    catch { toast.error('Could not read that file'); }
    e.target.value = '';
  };

  const run = async (dryRun) => {
    if (!csv.trim()) { toast.error('Upload the export or paste it in first'); return; }
    setBusy(dryRun ? 'check' : 'apply');
    try {
      const r = await spec.call(csv, dryRun);
      setResult({ ...r, dryRun });
      if (dryRun) {
        setChecked(csv);
        toast.success('Checked. Nothing was written — look at the numbers below.');
      } else {
        setChecked(null);
        toast.success(`Imported: ${r.inserted ?? r.paymentsToInsert ?? 0} added`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'That import failed');
    } finally { setBusy(''); }
  };

  const ready = checked === csv && csv.trim().length > 0;

  return (
    <Card className="p-5 border-slate-200 space-y-3" data-testid="gd-import">
      <div className="flex items-center gap-2 font-bold text-slate-900">
        <FileSpreadsheet className="w-4 h-4" style={{ color: GREEN }} />
        Bring in a GorillaDesk export
      </div>
      <p className="text-sm text-slate-600">
        For the invoice and payment exports, which do not use our column names. An invoice
        already on file is corrected rather than duplicated, so running the same export twice
        is safe.
      </p>

      <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1 w-fit">
        {Object.entries(KINDS).map(([k, v]) => (
          <button key={k} onClick={() => switchKind(k)}
            className={`px-3 py-1.5 rounded text-sm font-semibold transition ${kind === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid={`gd-kind-${k}`}>{v.label}</button>
        ))}
      </div>
      <p className="text-xs text-slate-500">{spec.hint}</p>

      <div className="flex gap-2 flex-wrap">
        <label className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-slate-200 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
          <FileSpreadsheet className="w-4 h-4" />Upload the CSV
          <input type="file" accept=".csv" className="hidden" onChange={onFile} data-testid="gd-file-input" />
        </label>
        <span className="text-xs text-slate-400 self-center">…or paste it below</span>
      </div>
      <textarea value={csv} onChange={(e) => onText(e.target.value)} rows={6}
        placeholder="Paste the export here, including its header row…"
        className="w-full text-xs font-mono border border-slate-200 rounded-md p-2"
        data-testid="gd-csv-input" />

      <div className="flex gap-2 flex-wrap items-center">
        <Button onClick={() => run(true)} disabled={!!busy} variant="outline" className="gap-1.5" data-testid="gd-check-btn">
          {busy === 'check' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          Check it first
        </Button>
        <Button onClick={() => run(false)} disabled={!!busy || !ready}
          className="gap-1.5 text-white" style={{ backgroundColor: ready ? GREEN : '#cbd5e1' }}
          data-testid="gd-apply-btn">
          {busy === 'apply' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Import for real
        </Button>
        {!ready && <span className="text-xs text-slate-500">Run the check first.</span>}
      </div>

      {result && (
        <Card className={`p-4 ${result.dryRun ? 'border-blue-200 bg-blue-50/60' : 'border-emerald-200 bg-emerald-50/60'}`} data-testid="gd-result">
          <div className={`flex items-center gap-2 font-bold ${result.dryRun ? 'text-blue-700' : 'text-emerald-700'}`}>
            {result.dryRun ? <Eye className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
            {result.dryRun ? 'Checked — nothing was written yet' : 'Imported'}
          </div>
          <div className="grid sm:grid-cols-3 gap-x-6 gap-y-1 mt-2 text-sm text-slate-700">
            {spec.counts(result).map(([label, value]) => (
              <div key={label} className="flex justify-between gap-2 border-b border-slate-200/70 py-0.5">
                <span className="text-slate-500">{label}</span>
                <span className="font-semibold tabular-nums" data-testid={`gd-count-${label}`}>{value ?? 0}</span>
              </div>
            ))}
          </div>
          {!result.dryRun && (
            <div className="text-sm text-slate-700 mt-2" data-testid="gd-applied">
              {result.inserted != null ? `${result.inserted} added, ${result.updated || 0} corrected. ` : ''}
              Nothing was deleted.
            </div>
          )}
          {spec.warnings(result).map((w, i) => (
            <div key={i} className="mt-2 text-sm text-amber-700 flex items-start gap-1" data-testid="gd-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />{w}
            </div>
          ))}
          {result.skipped?.length > 0 && (
            <ul className="list-disc pl-5 text-xs text-slate-600 mt-2">
              {result.skipped.slice(0, 6).map((row, i) => <li key={i}>{String(row)}</li>)}
            </ul>
          )}
        </Card>
      )}
    </Card>
  );
}
